/*
 * The admin shell and the sign-in gate.
 *
 * Everything under /admin renders inside this component, and the <Outlet /> is only
 * reached when the *server* has confirmed the signed-in account is a member of the
 * administrators group. The check is not a client-side inspection of the
 * token: GET /api/admin/me is what answers, having verified the signature, the
 * audience, the scope and the group.
 *
 * That means the gate is a convenience, not the security boundary - a modified
 * browser could render the admin UI, and every single request it made would be
 * refused. The boundary is requireAdmin on the server.
 *
 * The states this has to handle, each with something specific to say:
 *
 *   configuration missing   Entra ID is not set up on this server yet
 *   not signed in           offer sign-in
 *   signed in, not a member the interesting one: say that access is granted through
 *                           the directory and who to ask, rather than "forbidden" -
 *                           and never WHICH group. No directory group name reaches
 *                           the browser: its id was kept off the wire on 2026-08-27
 *                           and its name off the screen on 2026-09-22, both by the
 *                           owner's rule. The group is the deployment's business.
 *   groups overage          the token could not carry the group list at all, which
 *                           needs a different fix from "you are not a member"
 *
 * The header and the footer both sit OUTSIDE the gate. The light/dark switch in
 * the header is for whoever is looking, and the reader who followed "Manage"
 * and met the sign-in card is looking too.
 *
 * LANGUAGE (S48, 2026-10-29). The header carries the language dropdown, as the
 * public one does, and everything up to the gate follows it: the header and
 * footer, the progress lines, and the cards above - "Sign-in, sign-out and
 * similar pages", in the owner's words. The admin pages behind the gate stayed
 * English at first, by a decision the owner accepted; the same day the owner
 * asked for them too, and the sidebar and every page now read `t.admin`. The
 * diagnostic line in a configuration error, and the server's verdict in a
 * refused access check, are shown as they came - they quote English error
 * messages, and are read by whoever fixes the deployment.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ADMIN_RELEASE_NOTES_PATH } from '@accotool-kb/shared';
import { ADMIN_ATTACHMENTS_PATH } from './components/attachmentFiles.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ErrorBanner, Loading } from '../components/ui.js';
import { ApiError } from '../api/client.js';
import { SiteBrand, useSiteBranding } from '../site/SiteBranding.js';
import { SiteFooter } from '../site/SiteFooter.js';
import { ColorSchemeSwitch } from '../site/ColorSchemeSwitch.js';
import { LanguageSwitch } from '../site/LanguageSwitch.js';
import { SiteNav } from '../site/SiteNav.js';
import { SignedInAs } from '../site/SignedInAs.js';
import { useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';

export function AdminLayout(): JSX.Element {
  const auth = useAuth();
  const t = useMessages();

  /*
   * Public pages never fetch the Entra sign-in configuration; entering the admin
   * area is what requests it. A full page load on /admin is detected by the
   * AuthProvider itself (that is also where the sign-in redirect returns); this
   * covers the client-side navigation case. Idempotent.
   */
  useEffect(() => {
    auth.ensureSignInConfig();
  }, [auth]);
  const location = useLocation();

  // The admin shell has no bootstrap; a direct load of /admin has to ask for the
  // brand itself. Idempotent, and a no-op when the public shell already set it.
  const branding = useSiteBranding();
  useEffect(() => {
    branding.ensureLoaded();
  }, [branding]);

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t.common.skipToContent}
      </a>

      <header className="site-header">
        <div className="site-header-inner">
          <NavLink to="/" className="brand">
            <SiteBrand />
          </NavLink>
          <span className="tag">Admin</span>

          <span className="header-spacer" />

          <LanguageSwitch />

          <ColorSchemeSwitch />

          {/* The same Articles, Release Notes and Manage as the public header -
              Manage highlighted here, as the section the reader is in - in place
              of "View site" and the name (2026-10-09). The name is in the
              sidebar's Signed in as. Release Notes (S42, 2026-10-12) goes to the
              PUBLIC /releasenotes, as Articles goes to the public /; the admin
              list of release notes is the sidebar's "Release notes". */}
          <SiteNav shell="admin" />
        </div>
      </header>

      <main id="main">
        <div className="page">
          {!auth.ready && <Loading label={t.adminGate.preparingSignIn} />}

          {auth.ready && auth.configError && (
            <div className="notice notice-error" role="alert">
              <div className="notice-body">
                <strong>{auth.configError}</strong>
                <p>{richText(t.adminGate.configErrorHelp, { ready: <code>/api/health/ready</code> })}</p>
              </div>
            </div>
          )}

          {auth.ready && !auth.configError && auth.config && !auth.config.auth.configured && (
            <NotConfigured />
          )}

          {auth.ready && auth.config?.auth.configured && !auth.signedIn && <SignIn />}

          {auth.signedIn && auth.redirectingToSignIn && (
            <Loading label={t.adminGate.sessionExpired} />
          )}

          {auth.signedIn && !auth.redirectingToSignIn && auth.checkingIdentity && (
            <CheckingAccess />
          )}

          {auth.signedIn && !auth.checkingIdentity && !auth.identity?.isAdmin && (
            <NotAnAdmin
              message={auth.authorisationError}
              onRetry={auth.retryIdentity}
            />
          )}

          {auth.identity?.isAdmin && (
            <div className="admin-layout">
              <aside className="sidebar" aria-label={t.admin.shell.sidebar.label}>
                <nav className="admin-nav">
                  <NavLink to="/admin" end>
                    {t.admin.shell.sidebar.overview}
                  </NavLink>
                  <NavLink to="/admin/articles">{t.admin.shell.sidebar.articles}</NavLink>
                  {/* No `end`, so the editor and the new-note page stay highlighted. */}
                  <NavLink to={ADMIN_RELEASE_NOTES_PATH}>{t.admin.shell.sidebar.releaseNotes}</NavLink>
                  {/* The file library (S47): what articles list and link. */}
                  <NavLink to={ADMIN_ATTACHMENTS_PATH}>{t.admin.shell.sidebar.attachments}</NavLink>
                  <NavLink to="/admin/promoted">{t.admin.shell.sidebar.promoted}</NavLink>
                  {/* The front page's frequent questions (S51), beside the other front-page list. */}
                  <NavLink to="/admin/questions">{t.admin.shell.sidebar.questions}</NavLink>
                  <NavLink to="/admin/metadata">{t.admin.shell.sidebar.metadata}</NavLink>
                  <NavLink to="/admin/settings">{t.admin.shell.sidebar.settings}</NavLink>
                </nav>

                <SignedInAs />
              </aside>

              <section key={location.pathname}>
                <Outlet />
              </section>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

/**
 * The access check, with a deadline.
 *
 * GET /api/admin/me is one request against an already-open connection: it should
 * answer in well under a second. If it has not answered after ten, something is
 * wrong in a way that will not fix itself, and an indefinite spinner is the least
 * useful thing to show - it is indistinguishable from a client-side loop, which is
 * exactly the failure this replaced.
 */
function CheckingAccess(): JSX.Element {
  const t = useMessages();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 10_000);
    return () => clearTimeout(timer);
  }, []);

  if (!slow) return <Loading label={t.adminGate.checkingAccess} />;

  return (
    <div className="card" style={{ maxWidth: 640, margin: '3rem auto' }}>
      <h1>{t.adminGate.slowTitle}</h1>
      <p className="muted">{t.adminGate.slowIntro}</p>
      <p>{t.adminGate.slowLookAt}</p>
      <ul>
        <li>{richText(t.adminGate.slowNetwork, { me: <code>/api/admin/me</code> })}</li>
        <li>{richText(t.adminGate.slowHealth, { ready: <code>/api/health/ready</code> })}</li>
        <li>{t.adminGate.slowConsole}</li>
      </ul>
      <div className="button-row">
        <button type="button" className="button" onClick={() => window.location.reload()}>
          {t.adminGate.reload}
        </button>
      </div>
    </div>
  );
}

function SignIn(): JSX.Element {
  const auth = useAuth();
  const t = useMessages();

  return (
    <div className="card" style={{ maxWidth: 560, margin: '3rem auto' }}>
      <h1>{t.adminGate.signInTitle}</h1>
      <p className="muted">{t.adminGate.signInBody}</p>
      <button type="button" className="button button-primary" onClick={auth.signIn}>
        {t.adminGate.signInWithMicrosoft}
      </button>
      <p className="faint" style={{ marginTop: '1rem', marginBottom: 0 }}>
        {t.adminGate.readingNeedsNoSignIn}
      </p>
    </div>
  );
}

function NotConfigured(): JSX.Element {
  const t = useMessages();

  return (
    <div className="card" style={{ maxWidth: 720, margin: '3rem auto' }}>
      <h1>{t.adminGate.notConfiguredTitle}</h1>
      <p className="muted">{t.adminGate.notConfiguredBody}</p>
      <p>
        {richText(t.adminGate.notConfiguredFix, {
          tenant: <code>ENTRA_TENANT_ID</code>,
          api: <code>ENTRA_API_CLIENT_ID</code>,
          spa: <code>ENTRA_SPA_CLIENT_ID</code>,
          group: <code>ENTRA_ADMIN_GROUP_ID</code>,
          ready: <code>GET /api/health/ready</code>,
          doc: <code>docs/ENTRA-ID-SETUP.md</code>,
        })}
      </p>
    </div>
  );
}

function NotAnAdmin({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}): JSX.Element {
  const auth = useAuth();
  const t = useMessages();

  return (
    <div className="card" style={{ maxWidth: 720, margin: '3rem auto' }}>
      <h1>{t.adminGate.noAccessTitle}</h1>

      {message ? (
        <ErrorBanner error={new ApiError(403, 'not-an-admin', message)} onRetry={onRetry} />
      ) : (
        <p className="muted">{t.adminGate.noAccessNotMember}</p>
      )}

      <p>{t.adminGate.noAccessAsk}</p>

      <div className="button-row">
        <button type="button" className="button" onClick={onRetry}>
          {t.adminGate.checkAgain}
        </button>
        <button type="button" className="button button-quiet" onClick={auth.signOut}>
          {t.adminGate.signInAsSomeoneElse}
        </button>
      </div>
    </div>
  );
}
